<script setup lang="ts">
import { onMounted, ref } from 'vue';
import type { BaseLayer } from '@/map/mapStyle';

/**
 * The overlay panel on the map: attribution, weather, centre coordinates and a
 * base-layer picker.
 *
 * Converted from mapsuperinfo.vue. That component was constructed imperatively
 * with `new MapSuperInfo({ parent, data, propsData })` and shared its parent's
 * data object outright, so the two were coupled through mutable state. It now
 * takes props and emits an event, which is also what lets MapPanel mount it as a
 * MapLibre control without handing over its internals.
 */

const props = defineProps<{
  latitude: string;
  longitude: string;
  temperature: string;
  symbolCode: string;
  layer: BaseLayer;
}>();

const emit = defineEmits<{ 'layer-change': [layer: BaseLayer] }>();

const dropdown = ref<HTMLElement | null>(null);
const symbolFailed = ref(false);

onMounted(() => {
  if (!dropdown.value) {
    return;
  }

  window.$(dropdown.value).dropdown({
    onChange: (value: string) => emit('layer-change', value as BaseLayer),
  });
});

/**
 * MET publishes its icon set as files; the old code requested each one from the
 * retired weathericon 1.1 endpoint instead.
 *
 * The set is metno/weathericons (MIT), vendored under wwwroot/images/weather.
 * The @error handler still hides the image, so an unrecognised symbol code
 * degrades to no icon rather than a broken graphic.
 */
function symbolUrl(code: string): string {
  return `/images/weather/svg/${code}.svg`;
}
</script>

<template>
  <div id="mapsuperinfos" class="mapinfo">
    <div class="mapinfo-header">
      <div class="mapinfo-attribution">
        vær fra
        <a href="https://www.met.no/" target="_blank" rel="noopener">MET Norway</a>
      </div>
      <div v-show="props.symbolCode && !symbolFailed" class="mapinfo-symbol">
        <img
          :src="symbolUrl(props.symbolCode)"
          :alt="props.symbolCode"
          width="38"
          height="38"
          @error="symbolFailed = true"
        />
      </div>
      <div v-show="props.temperature" class="mapinfo-temp">{{ props.temperature }}&#8451;</div>
    </div>
    <div class="mapinfo-pos">
      <div class="left-column">
        <strong>Lat:</strong>
        <span title="WGS84 breddegrad (kartsenter)">{{ props.latitude }}</span>
      </div>
      <div class="right-column">
        <strong>Lon:</strong>
        <span title="WGS84 lengdegrad (kartsenter)">{{ props.longitude }}</span>
      </div>
    </div>
    <div class="mapinfo-footer">
      <div ref="dropdown" class="ui fluid selection dropdown">
        <input type="hidden" name="baselayer" :value="props.layer" />
        <i class="dropdown icon" />
        <div class="default text">Kart</div>
        <div class="menu">
          <div class="item" data-value="topo">Kart</div>
          <div class="item" data-value="greyscale">Gråtone</div>
        </div>
      </div>
    </div>
  </div>
</template>
